import Classifier, {
    AddClassifierGroupResult,
    ClassifierLibrary,
    ClassifierOutput,
    CreateClassifierReturn,
    ExtendedMetadataClassifier,
} from "models/ClassifierModel";
import { ClassifierResultAnswerTag } from "models/ClassifierResultAnswerTag";
import { AnswerPersistenceMode } from "models/AnswerPersistenceMode";
import { QuestionMetadataRequest } from "./QuestionMetadataService";
import { EddyEffectP2ResultsResponse } from "models/EddyEffectP2Results";
import { ClassifierResult } from "../models/ClassifierResult";
import { ClassifierResultValidation } from "../models/ClassifierResultValidation";
import { BaseService, ServiceError } from "./BaseService";
import { isNullableType } from "utils/TypeGuards";
import ClassifierGroup from "models/ClassifierGroup";
import PublishedModelResultsReconcileResult from "models/PublishedModelResultsReconcileResult";
import { SAID_WITHIN_MAX } from "constants/FullTextSearch";
import type {
    ModelChainResponse,
    UpdateModelComputeRelationsRequest,
    UpdateModelComputeRelationsResponse,
} from "models/LLMs/ModelChain";

interface IsFTSCompatible {
    success: true;
}

interface IsNotFTSCompatible {
    success: false;
    message: string;
}

type FTSCompatibleResult = IsFTSCompatible | IsNotFTSCompatible;

// Smart Predict and Question Metadata are mutually exclusive auto-scoring features for a question.
export type QuestionAutoScoreMode = "None" | "SmartPredict" | "Metadata";

export interface ConfigureAutoScoringRequest {
    mode: QuestionAutoScoreMode;
    automatedAnswerPersistenceMode: AnswerPersistenceMode;
    classifierResultAnswerTags: ClassifierResultAnswerTag[];
    questionMetadata: QuestionMetadataRequest | null;
}

export interface ClassifierInGroup {
    id: string;
    name: string;
    isPublished: boolean;
    hasModelResultsChart: boolean;
    /**
     * True only for LLM (non-extraction) models, which have reasoning KBo can analyze. Optional so a
     * response from a backend that predates this field doesn't force every model to non-analyzable.
     */
    isLlm?: boolean;
}

export interface SetClassifierGroupAssignment {
    classifierId: string;
    targetGroupId: string | null;
}

export interface SetClassifierGroupResult {
    classifierId: string;
    classifierName: string;
    targetGroupId: string | null;
    reconcile: PublishedModelResultsReconcileResult;
}

export interface SetClassifierGroupFailure {
    classifierId: string;
    classifierName?: string;
    message: string;
}

export interface SetClassifierGroupsResponse {
    results: SetClassifierGroupResult[];
    failures: SetClassifierGroupFailure[];
}

class ClassifierService extends BaseService {
    protected urlBase = "api/Classifier";

    public async getModelChain(
        classifierId: string,
    ): Promise<ModelChainResponse> {
        return this.get<ModelChainResponse>(
            new URLSearchParams(),
            `${classifierId}/model-chain`,
        );
    }

    public async updateModelComputeRelations(
        childClassifierId: string,
        request: UpdateModelComputeRelationsRequest,
    ): Promise<UpdateModelComputeRelationsResponse> {
        return this.put<UpdateModelComputeRelationsResponse>(
            new URLSearchParams(),
            `${childClassifierId}/model-compute-relations`,
            JSON.stringify(request),
        );
    }

    public async loadClassifiersForCurrentUser(organizationId?: string) {
        const apiPath = "GetForUser";
        return this.post<Classifier[]>(
            JSON.stringify({ organizationId }),
            new URLSearchParams({}),
            apiPath,
        );
    }

    public async getPublishedClassifiers(orgId: string) {
        const apiPath = "GetPublishedByOrgId";
        return this.get<Classifier[]>(
            new URLSearchParams({ orgId: orgId }),
            apiPath,
        );
    }

    public async configureAutoScoring(
        organizationId: string,
        questionId: string,
        request: ConfigureAutoScoringRequest,
    ) {
        await this.post(
            JSON.stringify(request),
            new URLSearchParams({
                organizationId,
                questionId,
            }),
            "ConfigureAutoScoring",
        );
    }

    public async getAutoScoringConfiguration(
        organizationId: string,
        questionId: string,
    ): Promise<ClassifierResultAnswerTag[]> {
        try {
            const params = new URLSearchParams({
                questionId: questionId,
            });

            const response = await this.post(
                JSON.stringify({ organizationId }),
                params,
                "GetAutoScoringConfiguration",
            );

            return response;
        } catch (error) {
            //@ts-ignore
            console.error(error.message);
            //@ts-ignore
            return error.message;
        }
    }

    public async updateClassifier(
        classifierName: string,
        userName: string,
        queryString: string,
        classifierId: string,
        classifierDescription?: string,
        classifierCategoryId?: string,
        classifierGroupId?: string,
    ) {
        const apiPath = "UpdateLuceneClassifier";
        const params = new URLSearchParams({});
        const body = {
            classifierName,
            userName,
            queryString,
            classifierId,
            classifierDescription,
            classifierCategoryId,
            classifierGroupId,
        };

        try {
            const response = await this.put(
                params,
                apiPath,
                JSON.stringify(body),
            );

            return response;
        } catch (error) {
            //@ts-ignore
            console.error(error.message);
            //@ts-ignore
            throw error;
        }
    }

    public async deleteClassifier(classifierId: string) {
        const apiPath = "";
        const params = new URLSearchParams({
            ClassifierId: classifierId,
        });
        return this.delete(params, apiPath);
    }

    public async createClassifier(
        name: string,
        searchString: string,
        orgId?: string,
        description?: string,
        categoryId?: string,
        groupId?: string,
    ) {
        const apiPath = "CreateLucene";
        const params = new URLSearchParams({
            orgId: orgId || "",
        });

        const body = {
            classifierName: name,
            queryString: searchString,
            classifierDescription: description,
            classifierCategoryId: categoryId,
            classifierGroupId: groupId,
        };

        return this.post<CreateClassifierReturn>(
            JSON.stringify(body),
            params,
            apiPath,
        );
    }

    public async publishClassifier(classifierId: string) {
        const apiPath = "Publish";
        const params = new URLSearchParams({
            ClassifierId: classifierId,
        });

        return this.get<Classifier>(params, apiPath);
    }

    public async unpublishClassifier(classifierId: string) {
        const apiPath = "Unpublish";
        const params = new URLSearchParams({
            ClassifierId: classifierId,
        });

        return this.get<Classifier>(params, apiPath);
    }

    public async getClassifierResultValidation(evaluationId: string) {
        const apiPath = "ClassificationValidation";
        const params = new URLSearchParams({
            evaluationId,
        });

        return this.get<ClassifierResultValidation[]>(params, apiPath);
    }

    public async positiveClassifications(audioMetadataId: string) {
        const apiPath = "PositiveClassifications";
        const params = new URLSearchParams({
            audioMetadataId,
        });

        return this.get<ClassifierResult[]>(params, apiPath);
    }

    public async allClassifications(audioMetadataId: string) {
        const apiPath = "AllClassifications";
        const params = new URLSearchParams({
            audioMetadataId,
        });

        return this.get<ClassifierResult[]>(params, apiPath);
    }
    public async getEddyEffectP2Results(audioMetadataId: string) {
        const apiPath = "EddyEffectP2Results";
        const params = new URLSearchParams({
            audioMetadataId,
        });
        return this.get<EddyEffectP2ResultsResponse[]>(params, apiPath);
    }

    public async isFTSCompatible(
        userQueryDefinition: string,
    ): Promise<FTSCompatibleResult> {
        const apiPath = "IsFTSCompatible";
        const params = new URLSearchParams();

        let errorMessage: string | undefined;

        try {
            await this.post(
                JSON.stringify(userQueryDefinition),
                params,
                apiPath,
            );
        } catch (error: unknown) {
            if (error instanceof ServiceError) {
                const match = error.message.match(/Message":"(.*?)"/);
                if (match && match[1]) {
                    errorMessage = match[1];
                }
            } else {
                errorMessage = String(error);
            }
        } finally {
            const lines = userQueryDefinition
                .split("\n")
                .filter((line) => line.length > 0);
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                let proximityIndex = line.indexOf("~");
                let foundError = false;

                // No "~" found on current line, skip proximity checking
                if (proximityIndex === -1) {
                    continue;
                }

                do {
                    // jump past the ~
                    proximityIndex += 1;

                    let nextSpaceIndex = line.indexOf(" ", proximityIndex);
                    if (nextSpaceIndex < 0) nextSpaceIndex = line.length;

                    const proximityNumber = Number.parseInt(
                        line.substring(proximityIndex, nextSpaceIndex),
                    );

                    // Bad proximity value, will be caught by isFTSCompatible, break out now to show og message
                    if (isNaN(proximityNumber)) {
                        foundError = true;
                        break;
                    }

                    if (
                        proximityNumber <= 0 ||
                        proximityNumber > SAID_WITHIN_MAX
                    ) {
                        return {
                            success: false,
                            message: `Line ${
                                i + 1
                            } failed with error: Error at character with position ${proximityIndex}: Specified proximity is not valid. Proximity must be (inclusive) between 1 and ${SAID_WITHIN_MAX}`,
                        };
                    }

                    // go to next ~ after the current one
                    proximityIndex = line.indexOf("~", proximityIndex + 1);
                } while (proximityIndex >= 0);

                if (foundError) break;
            }

            if (errorMessage) return { success: false, message: errorMessage };
            return { success: true };
        }
    }

    public async getUserClassifiers(
        organizationId?: string,
        isPublished?: boolean,
    ) {
        const apiPath = "GetForUser";
        const params = new URLSearchParams({});
        if (!isNullableType(isPublished)) {
            params.append("isPublished", isPublished.toString());
        }
        return this.post<Classifier[]>(
            JSON.stringify({ organizationId }),
            params,
            apiPath,
        );
    }

    public async getGroupIdsWithClassifierInLibrary(
        abortSignal?: AbortSignal,
    ): Promise<string[]> {
        const apiPath = "getGroupIdsWithClassifierInLibrary";
        return this.get(new URLSearchParams(), apiPath, abortSignal);
    }

    /**
     * Group ids that directly contain at least one classifier the current user can view. The chart's
     * group selector rolls this up over the group tree to hide groups the user has nothing to see in.
     */
    public async getGroupIdsWithViewableClassifiers(
        abortSignal?: AbortSignal,
    ): Promise<string[]> {
        const apiPath = "GetGroupIdsWithViewableClassifiers";
        return this.get(new URLSearchParams(), apiPath, abortSignal);
    }

    public async getClassifierGroups(abortSignal?: AbortSignal) {
        const apiPath = "GetClassifierGroups";
        const params = new URLSearchParams();
        return this.get<ClassifierGroup[]>(params, apiPath, abortSignal);
    }

    public async getClassifiersInGroup(
        groupId: string,
        abortSignal?: AbortSignal,
        includeSubGroups = false,
    ) {
        const apiPath = "GetClassifiersInGroup";
        const params = new URLSearchParams({ groupId });
        if (includeSubGroups) {
            params.set("includeSubGroups", "true");
        }
        return this.get<ClassifierInGroup[]>(params, apiPath, abortSignal);
    }

    public async setClassifierGroups(
        assignments: SetClassifierGroupAssignment[],
    ) {
        const apiPath = "SetClassifierGroups";
        return this.post<SetClassifierGroupsResponse>(
            JSON.stringify({ assignments }),
            new URLSearchParams(),
            apiPath,
        );
    }

    public async createClassifierGroup(name: string, parentGroupId?: string) {
        const apiPath = "CreateClassifierGroup";
        const params = new URLSearchParams({
            name,
        });
        if (parentGroupId) {
            params.set("parentGroupId", parentGroupId);
        }
        return this.get<ClassifierGroup>(params, apiPath);
    }

    public async updateClassifierGroup(id: string, name: string) {
        const apiPath = "UpdateClassifierGroup";
        const params = new URLSearchParams({
            id,
            name,
        });
        return this.get<ClassifierGroup>(params, apiPath);
    }

    public async deleteClassifierGroup(id: string) {
        const apiPath = "DeleteClassifierGroup";
        const params = new URLSearchParams({
            id,
        });
        return this.get<ClassifierGroup>(params, apiPath);
    }

    public async getClassifierCategories(abortSignal?: AbortSignal) {
        const apiPath = "GetClassifierCategories";
        const params = new URLSearchParams();
        return this.get<ClassifierGroup[]>(params, apiPath, abortSignal);
    }

    public async getClassifierLibrary() {
        const apiPath = "GetLuceneClassifierLibrary";
        const params = new URLSearchParams();
        return this.get<ClassifierLibrary>(params, apiPath);
    }

    public async getCoreClassifier(coreClassifierId: string) {
        const apiPath = "GetCoreClassifier";
        const params = new URLSearchParams({
            coreClassifierId,
        });
        return this.get<Classifier>(params, apiPath);
    }

    /**
     * Returns an object with two properties: "successfulClassifierNames" and "failedClassifierNames".
     * Respectively, the first property will contain classifier names that were successfully added to the tenant
     * and the second property will contain classifier names that were failed to be added due to a naming conflict
     * @param coreClassifierGroupId
     * @returns
     */
    public async addClassifiersFromCoreGroupToTenant(
        coreClassifierGroupId: string,
    ) {
        const apiPath = "AddClassifiersFromCoreGroupToTenant";
        const params = new URLSearchParams({
            coreClassifierGroupId,
        });
        return this.get<AddClassifierGroupResult>(params, apiPath);
    }

    public async addCoreClassifierToTenant(coreClassifierId: string) {
        const apiPath = "AddCoreClassifierToTenant";
        const params = new URLSearchParams({
            coreClassifierId,
        });
        return this.get(params, apiPath);
    }

    public async addClassifierToLibrary(coreClassifierId: string) {
        const apiPath = "AddCoreClassifierToLibrary";
        const params = new URLSearchParams({
            coreClassifierId,
        });
        return this.get(params, apiPath);
    }

    public async removeClassifierFromLibrary(coreClassifierId: string) {
        const apiPath = "RemoveCoreClassifierFromLibrary";
        const params = new URLSearchParams({
            coreClassifierId,
        });
        return this.get(params, apiPath);
    }

    // Model Results Graphs endpoints

    /**
     * Creates/updates the dashboard graph(s) for a published LLM classifier (individual chart for a
     * top-level/ungrouped model, or a grouped chart on the parent dashboard for a sub-group model).
     * @returns the reconcile result describing the resolved chart + dashboard.
     */
    public async createPublishedModelResultsSignalsGraphs(
        llmClassifierId: string,
    ): Promise<PublishedModelResultsReconcileResult> {
        const apiPath = `CreatePublishedModelResultsSignalsGraph`;
        const params = new URLSearchParams({
            ClassifierId: llmClassifierId,
        });
        return this.post<PublishedModelResultsReconcileResult>(
            "",
            params,
            apiPath,
        );
    }

    public async removePublishedModelResultsSignalsGraphs(
        llmClassifierId: string,
    ): Promise<PublishedModelResultsReconcileResult> {
        const apiPath = `RemovePublishedModelResultsSignalsGraph`;
        const params = new URLSearchParams({
            ClassifierId: llmClassifierId,
        });
        const response = await this.delete(params, apiPath);
        const result = await response.json().catch(() => null);

        return (
            result ?? {
                dashboardId: null,
                dashboardTitle: null,
                chartKind: 0,
                action: 0,
                chartTitle: null,
                affectedDashboardIds: [],
            }
        );
    }

    public async getExtendedMetadataStringClassifiers(
        showInactive = false,
    ): Promise<ExtendedMetadataClassifier[]> {
        const apiPath = "GetExtendedMetadataClassifiers";
        const params = new URLSearchParams({
            showInactive: showInactive.toString(),
        });
        const classifiers = await this.get<Classifier[]>(params, apiPath);
        return classifiers.map((classifier) =>
            this.mapClassifierToExtendedMetadata(classifier),
        );
    }

    public async getExtendedMetadataObjectClassifiers(
        showInactive = false,
    ): Promise<ExtendedMetadataClassifier[]> {
        const apiPath = "GetExtendedMetadataObjectClassifiers";
        const params = new URLSearchParams({
            showInactive: showInactive.toString(),
        });
        const classifiers = await this.get<Classifier[]>(params, apiPath);
        return classifiers.map((classifier) =>
            this.mapClassifierToExtendedMetadata(classifier),
        );
    }

    public async getAllExtendedMetadataClassifiers(
        showInactive = false,
    ): Promise<ExtendedMetadataClassifier[]> {
        const stringMetadata = await this.getExtendedMetadataStringClassifiers(
            showInactive,
        );
        const objectMetadata = await this.getExtendedMetadataObjectClassifiers(
            showInactive,
        );
        return [...stringMetadata, ...objectMetadata];
    }

    public async createExtendedMetadataClassifier({
        name,
        description,
        isIndexed,
        isGroupable,
        resultType,
        jsonSchema,
    }: Partial<ExtendedMetadataClassifier>) {
        const apiPath = "CreateExtendedMetadataClassifier";
        const params = new URLSearchParams();
        const body = {
            name,
            description,
            isIndexed,
            isGroupable,
            resultType,
            jsonSchema,
        };
        const classifier = await this.post<Classifier>(
            JSON.stringify(body),
            params,
            apiPath,
        );

        return this.mapClassifierToExtendedMetadata(classifier);
    }

    public async updateExtendedMetadataClassifier(
        updatedClassifier: ExtendedMetadataClassifier,
    ): Promise<ExtendedMetadataClassifier> {
        const apiPath = "UpdateExtendedMetadataClassifier";
        const params = new URLSearchParams();

        const classifier = await this.put<Classifier>(
            params,
            apiPath,
            JSON.stringify(updatedClassifier),
        );

        return this.mapClassifierToExtendedMetadata(classifier);
    }

    public async deactivateExtendedMetadataClassifiers(
        classifierIds: string[],
    ) {
        const apiPath = "DeactivateExtendedMetadataClassifiers";
        const params = new URLSearchParams();
        return this.post(JSON.stringify(classifierIds), params, apiPath);
    }

    private mapClassifierToExtendedMetadata(
        classifier: Classifier,
    ): ExtendedMetadataClassifier {
        return {
            id: classifier.id,
            name: classifier.name,
            description: classifier.description,
            isIndexed: classifier.isIndexed,
            isGroupable: classifier.isGroupable,
            isPublished: classifier.isPublished,
            isActive: classifier.isActive,
            resultType: classifier.resultType,
            classifierType: classifier.classifierTypeName,
            jsonSchema: classifier.classifierImplementations.find(
                (c) => c.isActive,
            )?.jsonSchema,
        };
    }

    public async getClassifierOutputs(classifierId: string) {
        const apiPath = "GetClassifierOutputs";
        const params = new URLSearchParams({
            classifierId,
        });
        return this.get<ClassifierOutput[]>(params, apiPath);
    }
}

export default ClassifierService;
